//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DirectLink } from '@/__configs/config_types';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';

type DropdownAccordionProps = {
  label: string;
  items?: DirectLink[];
};

const DropdownAccordion = ({ label, items = [] }: DropdownAccordionProps) => {
  const match = items.some((item) => !!('to' in item && useRouteMatch(item.to)));
  const [expanded, setExpanded] = useState<string | boolean>(match ? label : false);

  const handleChange = (panel: string) => (_: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <StyledAccordion disableGutters elevation={0} expanded={expanded === label} onChange={handleChange(label)}>
      <StyledAccordionSummary expandIcon={<PolarisIcon icon="down" />}>{label}</StyledAccordionSummary>
      <StyledAccordionDetails>
        {items.map((item, i) => (
          <StyledNavLink key={`${item.label}-${i}`} exact={item.exact} to={item.to}>
            {item.label}
          </StyledNavLink>
        ))}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};
export default DropdownAccordion;

const StyledAccordion = styled(Accordion)`
  background: none;
  box-shadow: none;
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  min-height: initial;
  display: inline-flex;
  width: 100%;
  text-transform: none;
  letter-spacing: 0;
  position: relative;
  font-size: 1.1rem;
  opacity: 0.7;
  text-decoration: none;
  transition: ease opacity 250ms;
  line-height: 1;
  color: ${({ theme }) => theme.palette.text.primary};
  flex-grow: initial;

  &:hover,
  &.active {
    opacity: 1;
    transition: ease opacity 250ms;
    background: inherit;
    color: ${({ theme }) => theme.palette.primary.light};
    opacity: 1;
  }

  &.MuiAccordionSummary-root {
    flex-direction: row;
    transform: none;
    padding: ${({ theme }) => theme.spacing(1, 0, 1, 4)};
  }

  & > .MuiAccordionSummary-expandIconWrapper {
    padding-right: ${({ theme }) => theme.spacing(4)};

    &.Mui-expanded {
      transform: scaleY(-1);
    }
  }
`;

const StyledNavLink = styled(NavLink)`
  &.active {
    color: ${({ theme }) => theme.palette.primary.light};
    opacity: 1;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  margin: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.background.paper};

  & a,
  & button {
    position: relative;
    opacity: 0.7;
    font-size: 1.1rem;
    text-decoration: none;
    transition: ease opacity 600ms;
    color: ${({ theme }) => theme.palette.text.primary};
    padding: ${({ theme }) => theme.spacing(1, 0)};

    &:hover,
    &.active {
      opacity: 1;
      transition: ease opacity 600ms;
      background: none;
    }

    &.active {
      color: ${({ theme }) => theme.palette.primary.light};
      background: none;
    }
  }

  &.MuiAccordionDetails-root {
    padding: ${({ theme }) => theme.spacing(1, 4, 1, 6)};
  }
`;
